import React from 'react';
import BlackCheckMarkIcon from 'app/assets/svgIcons/BlackCheckMarkIcon';
import { LegendInPlanningIcon } from 'app/assets/legend/LegendInPlanningIcon';
import type { MomentData } from '../../../services/momentsApi';
import type { SlideOutPanelCard } from '../types/slideOutPanel.types';
import type { NormalizedPhase } from '../containers/SlideoutCustomAccordionSection';
import { getSmartDropdownPosition } from '../utils/getSmartDropdownPosition';
import { resolveRiskLevel } from '../utils/resolveRiskLevel';
import {
  getPrimaryDateLabel,
  getSecondaryDateLabel,
  hasSecondaryDateStatus
} from '../configs/momentTypeDateFieldConfig';
import { normalizeStatus } from '../../../../utils/helpers';
import styles from '../SlideOutPanel.module.scss';

export type SlideoutFieldDropdownPortalsContext = {
  statusButtonRefs: React.MutableRefObject<
    Record<string, HTMLButtonElement | null>
  >;
  statusDropdownOpen: string | null;
  setStatusDropdownOpen: React.Dispatch<React.SetStateAction<string | null>>;
  riskButtonRefs: React.MutableRefObject<
    Record<string, HTMLButtonElement | null>
  >;
  riskLevelDropdownOpen: string | null;
  setRiskLevelDropdownOpen: React.Dispatch<React.SetStateAction<string | null>>;
  tierButtonRefs: React.MutableRefObject<
    Record<string, HTMLButtonElement | null>
  >;
  tierDropdownOpen: string | null;
  setTierDropdownOpen: React.Dispatch<React.SetStateAction<string | null>>;
  revenueImpactButtonRefs: React.MutableRefObject<
    Record<string, HTMLButtonElement | null>
  >;
  revenueImpactDropdownOpen: string | null;
  setRevenueImpactDropdownOpen: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  rtmButtonRefs: React.MutableRefObject<
    Record<string, HTMLButtonElement | null>
  >;
  rtmDropdownOpen: string | null;
  normalizedPhases: NormalizedPhase[];
  apiData: MomentData | null;
  card: SlideOutPanelCard | null | undefined;
  getGeneralInfo: (phaseId?: string) => Record<string, any>;
  tierOptions: string[];
  revenueImpactOptions: string[];
  handleStatusAction: (action: React.Key, phaseId: string) => Promise<void>;
  handleSecondaryStatusAction: (
    action: React.Key,
    phaseId: string
  ) => Promise<void>;
  momentType: string;
  handleRiskLevelAction: (action: React.Key, phaseId: string) => Promise<void>;
  handleTierAction: (action: React.Key, phaseId: string) => Promise<void>;
  handleRevenueImpactAction: (
    action: React.Key,
    phaseId: string
  ) => Promise<void>;
  handleRtmAction: (newRtm: string, phaseId: string) => Promise<void>;
};

export type SlideoutFieldDropdownPortals = {
  StatusDropdownPortal: React.FC<{ phaseId: string }>;
  RiskLevelDropdownPortal: React.FC<{ phaseId: string }>;
  MomentStatusDropdownPortal: React.FC<{ momentId: string }>;
  MomentRiskLevelDropdownPortal: React.FC<{ momentId: string }>;
  MomentTierDropdownPortal: React.FC<{ momentId: string }>;
  TierDropdownPortal: React.FC<{ phaseId: string }>;
  RtmDropdownPortal: React.FC<{ phaseId: string }>;
  RevenueImpactDropdownPortal: React.FC<{ phaseId: string }>;
};

/**
 * Title-cases a config date label ("RELEASE DATE") for use in a menu row
 * ("Release Date"), so the rows name their date without hardcoding the copy.
 */
function toRowLabel(label: string): string {
  return label
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * The verb for a status row: a date that is not complete can be completed, and one that
 * is complete can be reactivated. Each row follows its own status.
 */
function statusRowAction(status?: string): 'markComplete' | 'activate' {
  const normalized = normalizeStatus(status || '').toUpperCase();
  return normalized === 'COMPLETE' || normalized === 'COMPLETED'
    ? 'activate'
    : 'markComplete';
}

function statusRowVerb(status?: string): string {
  return statusRowAction(status) === 'activate'
    ? 'Activate'
    : 'Mark as Complete';
}

/**
 * Field dropdown portal components for phased + non-phased accordions (Part 3B-2).
 */
export function useSlideoutFieldDropdownPortals(
  ctx: SlideoutFieldDropdownPortalsContext
): SlideoutFieldDropdownPortals {
  const {
    statusButtonRefs,
    statusDropdownOpen,
    setStatusDropdownOpen,
    riskButtonRefs,
    riskLevelDropdownOpen,
    setRiskLevelDropdownOpen,
    tierButtonRefs,
    tierDropdownOpen,
    setTierDropdownOpen,
    revenueImpactButtonRefs,
    revenueImpactDropdownOpen,
    setRevenueImpactDropdownOpen,
    rtmButtonRefs,
    rtmDropdownOpen,
    normalizedPhases,
    apiData,
    card,
    getGeneralInfo,
    tierOptions,
    revenueImpactOptions,
    handleStatusAction,
    handleSecondaryStatusAction,
    momentType,
    handleRiskLevelAction,
    handleTierAction,
    handleRevenueImpactAction,
    handleRtmAction
  } = ctx;

  // Portal dropdown component for status with smart positioning
  const StatusDropdownPortal = ({ phaseId }: { phaseId: string }) => {
    // Move hooks to top level - always call them
    const [calculatedPosition, setCalculatedPosition] = React.useState({
      top: -9999, // Start off-screen to prevent flicker
      left: -9999,
      position: 'below'
    });
    const [isPositioned, setIsPositioned] = React.useState(false);

    // Get the button element for this phase to calculate smart positioning
    const buttonElement = statusButtonRefs.current[phaseId];
    // The menu grows from 2 rows (primary status + Remove Phase) to 3 (primary + secondary
    // date status + Remove Phase) whenever the moment type has a secondary date — computed here,
    // ahead of the hooks below, purely to size the smart-position height estimate correctly.
    // Using the stale 2-row height for a 3-row menu under-estimates the space it needs, so the
    // position calculation can decide "fits below" when it doesn't, and the menu renders tall
    // enough to overlap the row underneath it.
    const phaseForSizing = normalizedPhases.find(p => p.id === phaseId);
    const isNAPhaseForSizing =
      phaseForSizing?.status === 'N/A' || phaseForSizing?.status === 'n/a';
    const willShowSecondaryRowForSizing =
      !isNAPhaseForSizing &&
      hasSecondaryDateStatus(momentType) &&
      Boolean(getSecondaryDateLabel(momentType));
    const dropdownHeight = willShowSecondaryRowForSizing ? 180 : 120; // 3 items vs 2 items + padding (scaled 3:2)

    React.useEffect(() => {
      if (buttonElement && statusDropdownOpen === phaseId) {
        // Use requestAnimationFrame for better timing with DOM layout
        const calculatePosition = () => {
          // Ensure the element is visible and has proper dimensions
          if (buttonElement.offsetParent !== null) {
            const pos = getSmartDropdownPosition(
              buttonElement,
              177,
              dropdownHeight
            );
            setCalculatedPosition(pos);
          } else {
            // If element is not visible yet, try again on next frame
            requestAnimationFrame(calculatePosition);
          }
        };

        // Initial position calculation with small delay to ensure smooth positioning
        const rafId = requestAnimationFrame(() => {
          setTimeout(() => {
            calculatePosition();
            setIsPositioned(true);
          }, 10); // Small delay to ensure smooth positioning
        });

        // Add scroll listener to recalculate position when scrolling
        const handleScroll = () => {
          requestAnimationFrame(calculatePosition);
        };

        // Listen for scroll events on the slideout panel and window
        const slideoutPanel = document.querySelector(
          '[data-testid="sliding-panel-modal"]'
        );
        if (slideoutPanel) {
          slideoutPanel.addEventListener('scroll', handleScroll);
        }

        // Also listen for window scroll events
        window.addEventListener('scroll', handleScroll, true);

        return () => {
          cancelAnimationFrame(rafId);
          if (slideoutPanel) {
            slideoutPanel.removeEventListener('scroll', handleScroll);
          }
          window.removeEventListener('scroll', handleScroll, true);
        };
      }
    }, [buttonElement, dropdownHeight, phaseId, statusDropdownOpen]);

    // Early return after hooks
    if (statusDropdownOpen !== phaseId) {
      return null;
    }

    // Get phase-specific status to determine which options to show
    const currentPhase = normalizedPhases.find(p => p.id === phaseId);
    const phaseStatus = currentPhase?.status;

    // Determine which options to show based on phase status
    // ACTIVE phases: Show "Mark as Complete" and "Remove Phase"
    // COMPLETED phases: Show "Activate" and "Remove Phase"
    // N/A phases: Show only "Activate" (Remove Phase would be redundant as it makes status N/A)
    const isActivePhase = phaseStatus === 'ACTIVE' || phaseStatus === 'Active';
    const isNAPhase = phaseStatus === 'N/A' || phaseStatus === 'n/a';

    // The secondary date's own status row. Shown whenever the moment type has a secondary
    // date at all — never gated on showOnRoadmap ("Show Additional Card") or its state, so
    // the status can be managed without turning the additional card on. N/A phases have no
    // date to complete, so they keep the single Activate row.
    const secondaryDateLabel = getSecondaryDateLabel(momentType);
    const showSecondaryRow =
      !isNAPhase &&
      hasSecondaryDateStatus(momentType) &&
      Boolean(secondaryDateLabel);
    const secondaryStatus = (currentPhase as any)?.secondaryDateStatus;
    const primaryDateLabel = getPrimaryDateLabel(momentType);
    // Only name the primary date when there is a second row to distinguish it from.
    const primaryRowLabel =
      showSecondaryRow && primaryDateLabel
        ? ` - ${toRowLabel(primaryDateLabel)}`
        : '';

    // Count total non-N/A phases to enforce at least one active phase
    const activePhases = normalizedPhases.filter(
      p => p.status !== 'N/A' && p.status !== 'n/a'
    );
    const activePhaseCount = activePhases.length;
    const isCurrentPhaseActive = phaseStatus !== 'N/A' && phaseStatus !== 'n/a';

    // Disable "Remove Phase" if this is the only non-N/A phase
    const isLastActivePhase = isCurrentPhaseActive && activePhaseCount === 1;

    // Disable "Remove Phase" when only Internal Beta and one other phase exist
    const activePhaseNames = activePhases.map(p => p.phaseName);
    const hasInternalBeta = activePhaseNames.includes('Internal Beta');
    const currentPhaseName = currentPhase?.phaseName;
    const isInternalBetaRestriction =
      isCurrentPhaseActive &&
      activePhaseCount === 2 &&
      hasInternalBeta &&
      currentPhaseName !== 'Internal Beta';

    // Combine both disable conditions
    const shouldDisableRemove = isLastActivePhase || isInternalBetaRestriction;

    const position = calculatedPosition;
    // Determine if dropdown should flip above or below
    const isAbove = position.position === 'above';

    // Don't render until position is calculated to prevent flicker
    if (!isPositioned) {
      return null;
    }

    return (
      <div
        className={styles.statusDropdownContainer}
        style={{
          position: 'absolute',
          top: '30px',
          left: '0',
          zIndex: 999999, // Above all slideout content
          // Wider when the rows name their date ("Mark as Complete - Release Date")
          width: showSecondaryRow ? 'max-content' : '177px',
          minWidth: '177px',
          transformOrigin: isAbove ? 'bottom' : 'top'
        }}
      >
        <div className={styles.statusDropdownMenu}>
          {isActivePhase ? (
            // Options for ACTIVE phases
            <div
              className={styles.statusMenuItem}
              data-testid={`slideout-phase-primary-status-action-${phaseId}`}
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                handleStatusAction('markComplete', phaseId);
                setStatusDropdownOpen(null);
              }}
            >
              {`Mark as Complete${primaryRowLabel}`}
            </div>
          ) : (
            // Options for COMPLETED/N/A phases
            <div
              className={styles.statusMenuItem}
              data-testid={`slideout-phase-primary-status-action-${phaseId}`}
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                handleStatusAction('activate', phaseId);
                setStatusDropdownOpen(null);
              }}
            >
              {`Activate${primaryRowLabel}`}
            </div>
          )}
          {/* Secondary date status row — available whether or not the additional card is on */}
          {showSecondaryRow && (
            <div
              className={styles.statusMenuItem}
              data-testid={`slideout-phase-secondary-status-action-${phaseId}`}
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                handleSecondaryStatusAction(
                  statusRowAction(secondaryStatus),
                  phaseId
                );
                setStatusDropdownOpen(null);
              }}
            >
              {`${statusRowVerb(secondaryStatus)} - ${toRowLabel(
                secondaryDateLabel as string
              )}`}
            </div>
          )}
          {/* Remove Phase option - hide for N/A phases as it would be redundant */}
          {!isNAPhase && (
            <div
              className={`${styles.statusMenuItem} ${
                shouldDisableRemove ? styles.disabledMenuItem : ''
              }`}
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                // Don't allow removing phase if it's the last active phase or restricted by Internal Beta rule
                // if (shouldDisableRemove) {
                //   return;
                // }
                handleStatusAction('removePhase', phaseId);
                setStatusDropdownOpen(null);
              }}
            >
              Remove Phase
            </div>
          )}
        </div>
      </div>
    );
  };

  // Portal dropdown component for risk level with smart positioning
  const RiskLevelDropdownPortal = ({ phaseId }: { phaseId: string }) => {
    // Move hooks to top level - always call them
    const [calculatedPosition, setCalculatedPosition] = React.useState({
      top: -9999, // Start off-screen to prevent flicker
      left: -9999,
      position: 'below'
    });
    const [isPositioned, setIsPositioned] = React.useState(false);

    // Get the button element for this phase to calculate smart positioning
    const buttonElement = riskButtonRefs.current[phaseId];
    const dropdownHeight = 280; // Actual height of risk level dropdown (4 items + padding)

    React.useEffect(() => {
      if (buttonElement && riskLevelDropdownOpen === phaseId) {
        // Use requestAnimationFrame for better timing with DOM layout
        const calculatePosition = () => {
          // Ensure the element is visible and has proper dimensions
          if (buttonElement.offsetParent !== null) {
            const pos = getSmartDropdownPosition(
              buttonElement,
              235,
              dropdownHeight
            );
            setCalculatedPosition(pos);
          } else {
            // If element is not visible yet, try again on next frame
            requestAnimationFrame(calculatePosition);
          }
        };

        // Initial position calculation with small delay to ensure smooth positioning
        const rafId = requestAnimationFrame(() => {
          setTimeout(() => {
            calculatePosition();
            setIsPositioned(true);
          }, 10); // Small delay to ensure smooth positioning
        });

        // Add scroll listener to recalculate position when scrolling
        const handleScroll = () => {
          requestAnimationFrame(calculatePosition);
        };

        // Listen for scroll events on the slideout panel and window
        const slideoutPanel = document.querySelector(
          '[data-testid="sliding-panel-modal"]'
        );
        if (slideoutPanel) {
          slideoutPanel.addEventListener('scroll', handleScroll);
        }

        // Also listen for window scroll events
        window.addEventListener('scroll', handleScroll, true);

        return () => {
          cancelAnimationFrame(rafId);
          if (slideoutPanel) {
            slideoutPanel.removeEventListener('scroll', handleScroll);
          }
          window.removeEventListener('scroll', handleScroll, true);
        };
      }
    }, [buttonElement, dropdownHeight, phaseId, riskLevelDropdownOpen]);

    // Early return after hooks
    if (riskLevelDropdownOpen !== phaseId) return null;

    // Get phase-specific risk level for this dropdown
    const phaseGeneralInfo = getGeneralInfo(phaseId);
    const phaseRiskLevel = resolveRiskLevel(
      phaseGeneralInfo?.riskLevel,
      card?.riskLevel
    );

    const position = calculatedPosition;
    // Determine if dropdown should flip above or below
    const isAbove = position.position === 'above';

    // Don't render until position is calculated to prevent flicker
    if (!isPositioned) {
      return null;
    }

    return (
      <div
        className={styles.riskLevelDropdownContainer}
        style={{
          position: 'absolute',
          top: '30px',
          left: '0',
          zIndex: 999999, // Above all slideout content
          width: '235px',
          minWidth: '235px',
          transformOrigin: isAbove ? 'bottom' : 'top'
        }}
      >
        <div className={styles.riskLevelDropdownMenu}>
          <div
            className={styles.riskLevelMenuItem}
            onMouseDown={e => {
              e.stopPropagation();
              e.preventDefault();
              handleRiskLevelAction('low', phaseId);
              setRiskLevelDropdownOpen(null);
            }}
          >
            <div className={styles.riskLevelMenuItemContent}>
              <div className={styles.riskLevelMenuIcon}>
                <div className={styles.riskIconContainer}>
                  <div
                    className={`${styles.riskIcon} ${styles.riskIconLow}`}
                  ></div>
                </div>
              </div>
              <span>
                Risk Level: <strong>Low</strong>
              </span>
            </div>
            {(phaseRiskLevel?.toLowerCase() === 'low' ||
              phaseRiskLevel?.toLowerCase() === 'green') && (
              <BlackCheckMarkIcon
                width={18}
                height={14}
                className={styles.checkmark}
              />
            )}
          </div>
          <div
            className={styles.riskLevelMenuItem}
            onMouseDown={e => {
              e.stopPropagation();
              e.preventDefault();
              handleRiskLevelAction('moderate', phaseId);
              setRiskLevelDropdownOpen(null);
            }}
          >
            <div className={styles.riskLevelMenuItemContent}>
              <div className={styles.riskLevelMenuIcon}>
                <div className={styles.riskIconContainer}>
                  <div
                    className={`${styles.riskIcon} ${styles.riskIconModerate}`}
                  ></div>
                </div>
              </div>
              <span>
                Risk Level: <strong>Moderate</strong>
              </span>
            </div>
            {(phaseRiskLevel?.toLowerCase() === 'moderate' ||
              phaseRiskLevel?.toLowerCase() === 'yellow') && (
              <BlackCheckMarkIcon
                width={18}
                height={14}
                className={styles.checkmark}
              />
            )}
          </div>
          <div
            className={styles.riskLevelMenuItem}
            onMouseDown={e => {
              e.stopPropagation();
              e.preventDefault();
              handleRiskLevelAction('high', phaseId);
              setRiskLevelDropdownOpen(null);
            }}
          >
            <div className={styles.riskLevelMenuItemContent}>
              <div className={styles.riskLevelMenuIcon}>
                <div className={styles.riskIconContainer}>
                  <div
                    className={`${styles.riskIcon} ${styles.riskIconHigh}`}
                  ></div>
                </div>
              </div>
              <span>
                Risk Level: <strong>High</strong>
              </span>
            </div>
            {(phaseRiskLevel?.toLowerCase() === 'high' ||
              phaseRiskLevel?.toLowerCase() === 'red') && (
              <BlackCheckMarkIcon
                width={18}
                height={14}
                className={styles.checkmark}
              />
            )}
          </div>
          <div
            className={styles.riskLevelMenuItem}
            onMouseDown={e => {
              e.stopPropagation();
              e.preventDefault();
              handleRiskLevelAction('in_planning', phaseId);
              setRiskLevelDropdownOpen(null);
            }}
          >
            <div className={styles.riskLevelMenuItemContent}>
              <div className={styles.riskLevelMenuIcon}>
                <LegendInPlanningIcon width={20} height={20} />
              </div>
              <span>In Planning</span>
            </div>
            {(phaseRiskLevel?.toLowerCase() === 'planning' ||
              phaseRiskLevel?.toLowerCase() === 'in_planning' ||
              phaseRiskLevel?.toLowerCase() === 'in planning') && (
              <BlackCheckMarkIcon
                width={18}
                height={14}
                className={styles.checkmark}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  // Portal dropdown components for moments (non-phased layout)
  const MomentStatusDropdownPortal = ({ momentId }: { momentId: string }) => {
    // Early return after hooks
    if (statusDropdownOpen !== momentId) {
      return null;
    }

    // Get moment-specific status to determine which options to show (same logic as phased layout)
    const generalInfo = apiData?.generalInfo || getGeneralInfo();
    // For non-phased items, check both apiData.status and card.status (status is at root level)
    const momentStatus = apiData?.status || card?.status;
    const reportingStatus = generalInfo?.reportingStatus;

    // Determine which options to show based on moment status (same as phased layout)
    // Match the exact logic used in the status display (lines 1160-1165)
    // ACTIVE moments: Show "Mark as Complete" and "Remove Phase"
    // COMPLETED/N/A moments: Show "Activate" and "Remove Phase"
    const isActiveMoment =
      momentStatus === 'ACTIVE' ||
      momentStatus === 'Active' ||
      reportingStatus === 'ACTIVE' ||
      reportingStatus === 'Active' ||
      // If no specific status is set, default to active (same as status display logic)
      ((!momentStatus || momentStatus === '') &&
        (!reportingStatus || reportingStatus === ''));

    // Root-level secondary date status (non-phased Offers/PCO). Same rule as the phased
    // menu: shown because the moment type has a secondary date, never because the
    // additional card is on.
    const secondaryDateLabel = getSecondaryDateLabel(momentType);
    const showSecondaryRow =
      hasSecondaryDateStatus(momentType) && Boolean(secondaryDateLabel);
    const secondaryStatus = (apiData as any)?.secondaryDateStatus;
    const primaryDateLabel = getPrimaryDateLabel(momentType);
    const primaryRowLabel =
      showSecondaryRow && primaryDateLabel
        ? ` - ${toRowLabel(primaryDateLabel)}`
        : '';

    return (
      <div
        className={styles.statusDropdownContainer}
        style={{
          position: 'absolute',
          top: '30px',
          left: '0',
          zIndex: 999999,
          width: showSecondaryRow ? 'max-content' : '177px',
          minWidth: '177px'
        }}
      >
        <div className={styles.statusDropdownMenu}>
          {isActiveMoment ? (
            // Options for ACTIVE moments
            <div
              className={styles.statusMenuItem}
              data-testid={`slideout-moment-primary-status-action-${momentId}`}
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                handleStatusAction('markComplete', momentId);
                setStatusDropdownOpen(null);
              }}
            >
              {`Mark as Complete${primaryRowLabel}`}
            </div>
          ) : (
            // Options for COMPLETED/N/A moments
            <div
              className={styles.statusMenuItem}
              data-testid={`slideout-moment-primary-status-action-${momentId}`}
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                handleStatusAction('activate', momentId);
                setStatusDropdownOpen(null);
              }}
            >
              {`Activate${primaryRowLabel}`}
            </div>
          )}
          {/* Secondary date status row — available whether or not the additional card is on */}
          {showSecondaryRow && (
            <div
              className={styles.statusMenuItem}
              data-testid={`slideout-moment-secondary-status-action-${momentId}`}
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                handleSecondaryStatusAction(
                  statusRowAction(secondaryStatus),
                  momentId
                );
                setStatusDropdownOpen(null);
              }}
            >
              {`${statusRowVerb(secondaryStatus)} - ${toRowLabel(
                secondaryDateLabel as string
              )}`}
            </div>
          )}
        </div>
      </div>
    );
  };

  const MomentRiskLevelDropdownPortal = ({
    momentId
  }: {
    momentId: string;
  }) => {
    // Early return after hooks
    if (riskLevelDropdownOpen !== momentId) return null;

    // Get moment-specific risk level for this dropdown
    const generalInfo = apiData?.generalInfo || getGeneralInfo();
    const momentRiskLevel = resolveRiskLevel(
      generalInfo?.riskLevel,
      card?.riskLevel
    );

    return (
      <div
        className={styles.riskLevelDropdownContainer}
        style={{
          position: 'absolute',
          top: '30px',
          left: '0',
          zIndex: 999999
        }}
      >
        <div className={styles.riskLevelDropdownMenu}>
          <div
            className={styles.riskLevelMenuItem}
            onMouseDown={e => {
              e.stopPropagation();
              e.preventDefault();
              handleRiskLevelAction('low', momentId);
              setRiskLevelDropdownOpen(null);
            }}
          >
            <div className={styles.riskLevelMenuItemContent}>
              <div className={styles.riskLevelMenuIcon}>
                <div className={styles.riskIconContainer}>
                  <div
                    className={`${styles.riskIcon} ${styles.riskIconLow}`}
                  ></div>
                </div>
              </div>
              <span>
                Risk Level: <strong>Low</strong>
              </span>
            </div>
            {(momentRiskLevel?.toLowerCase() === 'low' ||
              momentRiskLevel?.toLowerCase() === 'green') && (
              <BlackCheckMarkIcon
                width={18}
                height={16}
                className={styles.checkmark}
              />
            )}
          </div>
          <div
            className={styles.riskLevelMenuItem}
            onMouseDown={e => {
              e.stopPropagation();
              e.preventDefault();
              handleRiskLevelAction('moderate', momentId);
              setRiskLevelDropdownOpen(null);
            }}
          >
            <div className={styles.riskLevelMenuItemContent}>
              <div className={styles.riskLevelMenuIcon}>
                <div className={styles.riskIconContainer}>
                  <div
                    className={`${styles.riskIcon} ${styles.riskIconModerate}`}
                  ></div>
                </div>
              </div>
              <span>
                Risk Level: <strong>Moderate</strong>
              </span>
            </div>
            {(momentRiskLevel?.toLowerCase() === 'moderate' ||
              momentRiskLevel?.toLowerCase() === 'yellow') && (
              <BlackCheckMarkIcon
                width={18}
                height={16}
                className={styles.checkmark}
              />
            )}
          </div>
          <div
            className={styles.riskLevelMenuItem}
            onMouseDown={e => {
              e.stopPropagation();
              e.preventDefault();
              handleRiskLevelAction('high', momentId);
              setRiskLevelDropdownOpen(null);
            }}
          >
            <div className={styles.riskLevelMenuItemContent}>
              <div className={styles.riskLevelMenuIcon}>
                <div className={styles.riskIconContainer}>
                  <div
                    className={`${styles.riskIcon} ${styles.riskIconHigh}`}
                  ></div>
                </div>
              </div>
              <span>
                Risk Level: <strong>High</strong>
              </span>
            </div>
            {(momentRiskLevel?.toLowerCase() === 'high' ||
              momentRiskLevel?.toLowerCase() === 'red') && (
              <BlackCheckMarkIcon
                width={18}
                height={16}
                className={styles.checkmark}
              />
            )}
          </div>
          <div
            className={styles.riskLevelMenuItem}
            onMouseDown={e => {
              e.stopPropagation();
              e.preventDefault();
              handleRiskLevelAction('in_planning', momentId);
              setRiskLevelDropdownOpen(null);
            }}
          >
            <div className={styles.riskLevelMenuItemContent}>
              <div className={styles.riskLevelMenuIcon}>
                <LegendInPlanningIcon width={20} height={20} />
              </div>
              <span>In Planning</span>
            </div>
            {(momentRiskLevel?.toLowerCase() === 'planning' ||
              momentRiskLevel?.toLowerCase() === 'in_planning' ||
              momentRiskLevel?.toLowerCase() === 'in planning') && (
              <BlackCheckMarkIcon
                width={18}
                height={16}
                className={styles.checkmark}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  const MomentTierDropdownPortal = ({ momentId }: { momentId: string }) => {
    // Move hooks to top level - always call them (same as phased)
    const [calculatedPosition, setCalculatedPosition] = React.useState({
      top: -9999, // Start off-screen to prevent flicker
      left: -9999,
      position: 'below'
    });
    const [isPositioned, setIsPositioned] = React.useState(false);

    // Get the button element for this moment to calculate smart positioning
    const buttonElement = tierButtonRefs.current[momentId];
    const dropdownHeight = 180; // Height of tier dropdown (4 items + padding)

    React.useEffect(() => {
      if (buttonElement && tierDropdownOpen === momentId) {
        // Use requestAnimationFrame for better timing with DOM layout
        const calculatePosition = () => {
          // Ensure the element is visible and has proper dimensions
          if (buttonElement.offsetParent !== null) {
            const pos = getSmartDropdownPosition(
              buttonElement,
              120, // Reduced width to better align with tier button
              dropdownHeight
            );
            setCalculatedPosition(pos);
          } else {
            // If element is not visible yet, try again on next frame
            requestAnimationFrame(calculatePosition);
          }
        };

        // Initial position calculation with small delay to ensure smooth positioning
        const rafId = requestAnimationFrame(() => {
          setTimeout(() => {
            calculatePosition();
            setIsPositioned(true);
          }, 10); // Small delay to ensure smooth positioning
        });

        // Add scroll listener to recalculate position when scrolling
        const handleScroll = () => {
          requestAnimationFrame(calculatePosition);
        };

        // Listen for scroll events on the slideout panel and window
        const slideoutPanel = document.querySelector(
          '[data-testid="sliding-panel-modal"]'
        );
        if (slideoutPanel) {
          slideoutPanel.addEventListener('scroll', handleScroll);
        }

        // Also listen for window scroll events
        window.addEventListener('scroll', handleScroll, true);

        return () => {
          cancelAnimationFrame(rafId);
          if (slideoutPanel) {
            slideoutPanel.removeEventListener('scroll', handleScroll);
          }
          window.removeEventListener('scroll', handleScroll, true);
        };
      }
    }, [buttonElement, dropdownHeight, momentId, tierDropdownOpen]);

    // Early return after hooks
    if (tierDropdownOpen !== momentId) return null;

    const generalInfo = apiData?.generalInfo || getGeneralInfo();
    const currentTier = generalInfo?.tier;
    // Use tier options from state (fetched from form template API)
    const tiers = tierOptions;

    const position = calculatedPosition;
    // Determine if dropdown should flip above or below
    const isAbove = position.position === 'above';

    // Don't render until position is calculated to prevent flicker
    if (!isPositioned) {
      return null;
    }

    return (
      <div
        className={styles.tierDropdownContainer}
        style={{
          position: 'absolute',
          top: '30px',
          left: '0',
          zIndex: 999999, // Above all slideout content
          width: '120px',
          transformOrigin: isAbove ? 'bottom' : 'top'
        }}
      >
        <div className={styles.tierDropdownMenu}>
          {tiers.map(tier => (
            <div
              key={tier}
              className={styles.tierMenuItem}
              onMouseDown={e => {
                e.stopPropagation();
                e.preventDefault();
                handleTierAction(tier, momentId);
                setTierDropdownOpen(null);
              }}
            >
              <span>{tier}</span>
              {currentTier === tier && (
                <BlackCheckMarkIcon className={styles.checkmark} />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Portal dropdown component for tier with smart positioning
  const TierDropdownPortal = ({ phaseId }: { phaseId: string }) => {
    // Move hooks to top level - always call them
    const [calculatedPosition, setCalculatedPosition] = React.useState({
      top: -9999, // Start off-screen to prevent flicker
      left: -9999,
      position: 'below'
    });
    const [isPositioned, setIsPositioned] = React.useState(false);

    // Get the button element for this phase to calculate smart positioning
    const buttonElement = tierButtonRefs.current[phaseId];
    const dropdownHeight = 180; // Height of tier dropdown (4 items + padding)

    React.useEffect(() => {
      if (buttonElement && tierDropdownOpen === phaseId) {
        // Use requestAnimationFrame for better timing with DOM layout
        const calculatePosition = () => {
          // Ensure the element is visible and has proper dimensions
          if (buttonElement.offsetParent !== null) {
            const pos = getSmartDropdownPosition(
              buttonElement,
              120, // Reduced width to better align with tier button
              dropdownHeight
            );
            setCalculatedPosition(pos);
          } else {
            // If element is not visible yet, try again on next frame
            requestAnimationFrame(calculatePosition);
          }
        };

        // Initial position calculation with small delay to ensure smooth positioning
        const rafId = requestAnimationFrame(() => {
          setTimeout(() => {
            calculatePosition();
            setIsPositioned(true);
          }, 10); // Small delay to ensure smooth positioning
        });

        // Add scroll listener to recalculate position when scrolling
        const handleScroll = () => {
          requestAnimationFrame(calculatePosition);
        };

        // Listen for scroll events on the slideout panel and window
        const slideoutPanel = document.querySelector(
          '[data-testid="sliding-panel-modal"]'
        );
        if (slideoutPanel) {
          slideoutPanel.addEventListener('scroll', handleScroll);
        }

        // Also listen for window scroll events
        window.addEventListener('scroll', handleScroll, true);

        return () => {
          cancelAnimationFrame(rafId);
          if (slideoutPanel) {
            slideoutPanel.removeEventListener('scroll', handleScroll);
          }
          window.removeEventListener('scroll', handleScroll, true);
        };
      }
    }, [buttonElement, dropdownHeight, phaseId, tierDropdownOpen]);

    // Early return after hooks
    if (tierDropdownOpen !== phaseId) return null;

    // Get phase-specific tier for this dropdown
    const phaseGeneralInfo = getGeneralInfo(phaseId);
    const phaseTier = phaseGeneralInfo?.tier;

    const position = calculatedPosition;
    // Determine if dropdown should flip above or below
    const isAbove = position.position === 'above';

    // Don't render until position is calculated to prevent flicker
    if (!isPositioned) {
      return null;
    }

    // Use tier options from state (fetched from form template API)
    const tierOptionsToRender = tierOptions;

    return (
      <div
        className={styles.tierDropdownContainer}
        style={{
          position: 'absolute',
          top: '30px',
          left: '0',
          zIndex: 999999, // Above all slideout content
          width: '120px',
          minWidth: '120px',
          transformOrigin: isAbove ? 'bottom' : 'top',
          pointerEvents: 'auto' as const
        }}
      >
        <div className={styles.tierDropdownMenu}>
          {tierOptionsToRender.map(tier => (
            <div
              key={tier}
              className={styles.tierMenuItem}
              style={{
                cursor: 'pointer'
              }}
              onMouseDown={e => {
                e.stopPropagation();
                e.preventDefault();
                handleTierAction(tier, phaseId);
                setTierDropdownOpen(null);
              }}
            >
              <span>{tier}</span>
              {phaseTier === tier && (
                <BlackCheckMarkIcon className={styles.checkmark} />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // RTM (Route-to-Market) Dropdown Portal â€” Price Changes and Offers
  const RtmDropdownPortal = ({ phaseId }: { phaseId: string }) => {
    const [calculatedPosition, setCalculatedPosition] = React.useState({
      top: -9999,
      left: -9999,
      position: 'below'
    });
    const [isPositioned, setIsPositioned] = React.useState(false);

    const buttonElement = rtmButtonRefs.current[phaseId];
    const dropdownHeight = 160; // ~4 options + padding

    React.useEffect(() => {
      if (buttonElement && rtmDropdownOpen === phaseId) {
        const calculatePosition = () => {
          if (buttonElement.offsetParent !== null) {
            const pos = getSmartDropdownPosition(
              buttonElement,
              140,
              dropdownHeight
            );
            setCalculatedPosition(pos);
          } else {
            requestAnimationFrame(calculatePosition);
          }
        };

        const rafId = requestAnimationFrame(() => {
          setTimeout(() => {
            calculatePosition();
            setIsPositioned(true);
          }, 10);
        });

        const handleScroll = () => {
          requestAnimationFrame(calculatePosition);
        };

        const slideoutPanel = document.querySelector(
          '[data-testid="sliding-panel-modal"]'
        );
        if (slideoutPanel) {
          slideoutPanel.addEventListener('scroll', handleScroll);
        }
        window.addEventListener('scroll', handleScroll, true);

        return () => {
          cancelAnimationFrame(rafId);
          if (slideoutPanel) {
            slideoutPanel.removeEventListener('scroll', handleScroll);
          }
          window.removeEventListener('scroll', handleScroll, true);
        };
      }
    }, [buttonElement, dropdownHeight, phaseId]);

    if (rtmDropdownOpen !== phaseId) return null;
    if (!isPositioned) return null;

    const currentPhase = normalizedPhases.find(p => p.id === phaseId);
    const currentRtm = currentPhase?.phaseName || '';

    // RTM options from the Price Changes and Offers create form
    const rtmOptions = ['VIP', 'Mobile', 'ETLA', 'A.com Direct'];
    // Exclude options already used by other phases (each RTM can only be used once)
    const usedRtms = normalizedPhases
      .filter(p => p.id !== phaseId)
      .map(p => p.phaseName);
    const availableOptions = rtmOptions.filter(o => !usedRtms.includes(o));

    const isAbove = calculatedPosition.position === 'above';

    return (
      <div
        className={styles.rtmDropdownContainer}
        style={{
          position: 'absolute',
          top: '30px',
          left: '0',
          zIndex: 999999,
          width: '140px',
          minWidth: '140px',
          transformOrigin: isAbove ? 'bottom' : 'top',
          pointerEvents: 'auto' as const
        }}
      >
        <div className={styles.rtmDropdownMenu}>
          {availableOptions.map(option => (
            <div
              key={option}
              className={styles.rtmMenuItem}
              style={{ cursor: 'pointer' }}
              onMouseDown={e => {
                e.stopPropagation();
                e.preventDefault();
                handleRtmAction(option, phaseId);
              }}
            >
              <span>{option}</span>
              {currentRtm === option && (
                <BlackCheckMarkIcon className={styles.checkmark} />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // $ Revenue Impact Dropdown Portal Component (shared for both phased and non-phased)
  const RevenueImpactDropdownPortal = ({ phaseId }: { phaseId: string }) => {
    // Move hooks to top level - always call them
    const [calculatedPosition, setCalculatedPosition] = React.useState({
      top: -9999, // Start off-screen to prevent flicker
      left: -9999,
      position: 'below'
    });
    const [isPositioned, setIsPositioned] = React.useState(false);

    // Get the button element for this phase/moment to calculate smart positioning
    const buttonElement = revenueImpactButtonRefs.current[phaseId];
    const dropdownHeight = 100; // Height of revenue driver dropdown (2 items + padding)

    React.useEffect(() => {
      if (buttonElement && revenueImpactDropdownOpen === phaseId) {
        // Use requestAnimationFrame for better timing with DOM layout
        const calculatePosition = () => {
          // Ensure the element is visible and has proper dimensions
          if (buttonElement.offsetParent !== null) {
            const pos = getSmartDropdownPosition(
              buttonElement,
              200, // Width to accommodate "$ Yes/No"
              dropdownHeight
            );
            setCalculatedPosition(pos);
          } else {
            // If element is not visible yet, try again on next frame
            requestAnimationFrame(calculatePosition);
          }
        };

        // Initial position calculation with small delay to ensure smooth positioning
        const rafId = requestAnimationFrame(() => {
          setTimeout(() => {
            calculatePosition();
            setIsPositioned(true);
          }, 10); // Small delay to ensure smooth positioning
        });

        // Add scroll listener to recalculate position when scrolling
        const handleScroll = () => {
          requestAnimationFrame(calculatePosition);
        };

        // Listen for scroll events on the slideout panel and window
        const slideoutPanel = document.querySelector(
          '[data-testid="sliding-panel-modal"]'
        );
        if (slideoutPanel) {
          slideoutPanel.addEventListener('scroll', handleScroll);
        }

        // Also listen for window scroll events
        window.addEventListener('scroll', handleScroll, true);

        return () => {
          cancelAnimationFrame(rafId);
          if (slideoutPanel) {
            slideoutPanel.removeEventListener('scroll', handleScroll);
          }
          window.removeEventListener('scroll', handleScroll, true);
        };
      }
    }, [buttonElement, dropdownHeight, phaseId, revenueImpactDropdownOpen]);

    // Early return after hooks
    if (revenueImpactDropdownOpen !== phaseId) return null;

    // âœ… UNIFIED: Get revenue impact value for both phased and non-phased
    // Check if this is a non-phased moment (phaseId === issueId)
    const isNonPhased = phaseId === (card?.issueId || 'moment-section');

    let currentRevenueImpact: string;
    if (isNonPhased) {
      // Non-phased: get from generalInfo
      const generalInfo = apiData?.generalInfo || getGeneralInfo();
      const booleanValue = generalInfo?.revenueImpact;
      currentRevenueImpact =
        booleanValue !== undefined && booleanValue !== null
          ? booleanValue === true || booleanValue === 'Yes'
            ? 'Yes'
            : 'No'
          : 'No'; // âœ… Default to 'No' for non-phased (most moments don't impact revenue)
    } else {
      // Phased: get from phase-specific general info
      const phaseGeneralInfo = getGeneralInfo(phaseId);
      const booleanValue = phaseGeneralInfo?.revenueImpact;
      currentRevenueImpact =
        booleanValue !== undefined && booleanValue !== null
          ? booleanValue === true || booleanValue === 'Yes'
            ? 'Yes'
            : 'No'
          : 'Yes'; // âœ… Default to 'Yes' for phased (GA/Public Beta have revenue impact by default)
    }

    const position = calculatedPosition;
    // Determine if dropdown should flip above or below
    const isAbove = position.position === 'above';

    // Don't render until position is calculated to prevent flicker
    if (!isPositioned) {
      return null;
    }

    return (
      <div
        className={styles.tierDropdownContainer}
        style={{
          position: 'absolute',
          top: '30px',
          left: '0',
          zIndex: 999999, // Above all slideout content
          width: '200px',
          minWidth: '200px',
          transformOrigin: isAbove ? 'bottom' : 'top',
          pointerEvents: 'auto' as const
        }}
      >
        <div className={styles.tierDropdownMenu}>
          {revenueImpactOptions.map(option => (
            <div
              key={option}
              className={styles.tierMenuItem}
              style={{
                cursor: 'pointer'
              }}
              onMouseDown={e => {
                e.stopPropagation();
                e.preventDefault();
                handleRevenueImpactAction(option, phaseId);
                setRevenueImpactDropdownOpen(null);
              }}
            >
              <span>{option}</span>
              {currentRevenueImpact === option && (
                <BlackCheckMarkIcon className={styles.checkmark} />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return {
    StatusDropdownPortal,
    RiskLevelDropdownPortal,
    MomentStatusDropdownPortal,
    MomentRiskLevelDropdownPortal,
    MomentTierDropdownPortal,
    TierDropdownPortal,
    RtmDropdownPortal,
    RevenueImpactDropdownPortal
  };
}
